import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Button, Badge, useAppSettings } from 'src/common';
import { Environment } from 'src/common/types';
import { AppGrid } from './AppGrid';
import { NotificationBanner } from './NotificationBanner';
import aiLogoSvg from '../images/lab-svgrepo-com.svg';
import gridIconSvg from '../images/grid-2-svgrepo-com.svg';

import {
  Search,
} from 'lucide-react';

interface AIPlaygroundInterfaceProps {
  onBackToHome: () => void;
}

export function AIPlaygroundInterface({ onBackToHome }: AIPlaygroundInterfaceProps) {
  const navigate = useNavigate();
  const { theme, developerMode, environment } = useAppSettings();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAppGrid, setShowAppGrid] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
  };

  const quickSuggestions = [
    "Launch Rogers VQA app",
  ];

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <NotificationBanner createNewSession={true} />

      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">AI Playground</span>
            <Badge className={environment === Environment.PROTOTYPE
              ? "bg-orange-500/20 text-orange-400 light:text-orange-600 border-orange-500/30 text-xs"
              : "bg-blue-500/20 text-blue-400 light:text-blue-600 border-blue-500/30 text-xs"
            }>
              {environment === Environment.PROTOTYPE ? 'Prototype' : 'Prod'}
            </Badge>
            {developerMode && (
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                Dev Tool
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="p-2 hover:bg-muted"
            onClick={() => setShowAppGrid(!showAppGrid)}
          >
            <img
              src={gridIconSvg}
              alt="Grid"
              className={`w-5 h-5 ${theme === 'dark' ? 'theme-filter-dark' : 'theme-filter-light'}`}
            />
          </Button>
        </div>
      </div>

      {/* App Grid Overlay */}
      {showAppGrid && (
        <AppGrid
          onClose={() => setShowAppGrid(false)}
          onBackToPlayground={() => setShowAppGrid(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center px-4 min-height-calc">
        {
          <>
            {/* Logo and Title */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <img
                  src={aiLogoSvg}
                  alt="AI Logo"
                  className={`w-11 h-11 -mt-2 ${theme === 'dark' ? 'theme-filter-icon-dark' : 'theme-filter-icon-light'}`}
                />
                <h1 className="text-4xl">AI Playground</h1>
              </div>
            </div>

            {/* Search Box */}
            <div className="w-full max-w-lg mb-8">
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <Search className="w-5 h-5 text-muted-foreground" />
                </div>
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Ask AI Playground..."
                  className="w-full bg-muted border-border pl-10 pr-4 py-3 rounded-full text-center focus:border-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />

              </div>
            </div>



            {/* Quick Suggestions */}
            <div className="w-full max-w-lg">
              <p className="text-sm text-muted-foreground mb-3 text-center">Try asking:</p>
              <div className="space-y-2">
                {quickSuggestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="ghost"
                    className="w-full text-left justify-start text-blue-400 light:text-blue-600 hover:text-blue-300 light:hover:text-blue-700"
                    onClick={() => {
                      if (suggestion === "Launch Rogers VQA app") {
                        navigate('/rogers-vqa');
                      } else {
                        setSearchQuery(suggestion);
                      }
                    }}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          </>
        }
      </div>

      {/* Bottom Home Indicator */}
      <div className="fixed bottom-2 left-1/2 transform -translate-x-1/2">
        <div className="w-32 h-1 bg-foreground rounded-full opacity-60"></div>
      </div>
    </div>
  );
}